import React, { useState } from 'react';
import { useAtomValue } from 'jotai';
import { authState } from '../store/atoms';
import { useAuth } from '../hooks/useAuth';
import { userAPI } from '../services';
import { User } from '../types/api';
import Modal from './Modal';

export type AccountModalMode = 'email' | 'password' | null;

interface AccountModalsProps {
  mode: AccountModalMode;
  onClose: () => void;
}

// 이메일 변경 / 비밀번호 변경 모달 (비회원 시안의 로그인 카드 디자인)
const AccountModals: React.FC<AccountModalsProps> = ({ mode, onClose }) => {
  const auth = useAtomValue(authState);
  const { updateUserInfo } = useAuth();
  const currentEmail = (auth.userInfo as User)?.email || '';

  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    setNewEmail('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    onClose();
  };

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) {
      alert('새 이메일을 입력해주세요.');
      return;
    }
    if (newEmail.trim() === currentEmail) {
      alert('현재 이메일과 동일합니다.');
      return;
    }
    try {
      setSubmitting(true);
      const resp = await userAPI.updateEmail(newEmail.trim());
      if (resp.success) {
        alert('이메일이 변경되었습니다.');
        if (auth.userInfo) {
          updateUserInfo({ ...auth.userInfo, email: newEmail.trim() });
        }
        close();
      } else {
        alert(resp.message || '이메일 변경에 실패했습니다.');
      }
    } catch {
      alert('이메일 변경에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) {
      alert('필수 항목을 입력해주세요.');
      return;
    }
    if (newPassword.length < 6) {
      alert('새 비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    if (newPassword !== confirmPassword) {
      alert('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    try {
      setSubmitting(true);
      const resp = await userAPI.changePassword({ currentPassword, newPassword });
      if (resp.success) {
        alert('비밀번호가 변경되었습니다.');
        close();
      } else {
        alert(resp.message || '비밀번호 변경에 실패했습니다.');
      }
    } catch {
      alert('비밀번호 변경에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* 이메일 변경 */}
      <Modal isOpen={mode === 'email'} onClose={close} size="sm" showCloseButton={false}>
        <div className="account-modal">
          <div className="account-modal-head">
            <div className="head-title">이메일 변경</div>
            <div className="head-sub">알림과 로그인에 사용할 이메일을 변경합니다</div>
            <button type="button" className="head-close" onClick={close} aria-label="닫기">✕</button>
          </div>
          <form className="account-modal-body" onSubmit={submitEmail}>
            <div className="field">
              <div className="field-label">현재 이메일</div>
              <div className="field-static">{currentEmail || '—'}</div>
            </div>
            <div className="field">
              <div className="field-label">새 이메일</div>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="새 이메일 주소 입력"
                autoFocus
              />
            </div>
            <button type="submit" className="submit-btn" disabled={submitting || !newEmail.trim()}>
              {submitting ? '변경 중...' : '이메일 변경'}
            </button>
          </form>
        </div>
      </Modal>

      {/* 비밀번호 변경 */}
      <Modal isOpen={mode === 'password'} onClose={close} size="sm" showCloseButton={false}>
        <div className="account-modal">
          <div className="account-modal-head">
            <div className="head-title">비밀번호 변경</div>
            <div className="head-sub">현재 비밀번호 확인 후 새 비밀번호로 변경합니다</div>
            <button type="button" className="head-close" onClick={close} aria-label="닫기">✕</button>
          </div>
          <form className="account-modal-body" onSubmit={submitPassword}>
            <div className="field">
              <div className="field-label">현재 비밀번호</div>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="현재 비밀번호 입력"
                autoFocus
              />
            </div>
            <div className="field">
              <div className="field-label">새 비밀번호</div>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="6자 이상"
              />
            </div>
            <div className="field">
              <div className="field-label">새 비밀번호 확인</div>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="새 비밀번호 다시 입력"
              />
            </div>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || !currentPassword || !newPassword || !confirmPassword}
            >
              {submitting ? '변경 중...' : '비밀번호 변경'}
            </button>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default AccountModals;
